import { useState } from "react";
import { CogIcon, CheckCircle2, Flag, ClipboardList, X, GitBranch } from "lucide-react";
import { FeatureFlagsTab } from "./FeatureFlagsDebugTab";
import { QuestionnaireAnswersToSubmitTab } from "./QuestionnaireAnswersDebugTab";
import { PrescreeningTab } from "./PrescreeningDebugTab";
import { MultiStudyDebugTab } from "./MultiStudyDebugTab";

const DEV_TOOLS_ENABLED_KEY = "developer_debug_tools_enabled";

type DebugTab = "flags" | "questionnaireAnswers" | "prescreening" | "multiStudy";

const isDev = import.meta.env.DEV;

/**
 * A simple debug panel that visualizes data for feature flags, questionnaires, and prescreening
 * Enable in production by opening the console and running:
 * localStorage.setItem("developer_debug_tools_enabled", "true")
 */
export function DebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<DebugTab>("flags");

  // Check localStorage for developer mode enabled flag
  const isDevModeEnabled = (() => {
    try {
      return localStorage.getItem(DEV_TOOLS_ENABLED_KEY) === "true";
    } catch {
      return false;
    }
  })();

  // Only show in development or when dev mode is enabled (We've configured netlify to show this on feature/branch deploys)
  if (!isDev && !isDevModeEnabled) {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        className="fixed bottom-4 right-4 bg-blue-500 text-white p-2 rounded-full shadow-lg z-50 flex items-center justify-center hover:bg-blue-600 transition-colors"
        onClick={() => setIsOpen(true)}
        title="Open Debug Panel"
        id="debug-panel-button"
      >
        <CogIcon size={18} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
      <div className="bg-blue-500 text-white px-4 py-2 flex justify-between items-center">
        <h3 className="text-sm font-medium">Developer Debug Tools</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-blue-600 rounded"
            title="Close Panel"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          className={`flex-1 py-2 px-2 text-xs font-medium ${activeTab === "flags" ? "bg-blue-50 text-blue-600 border-b-2 border-blue-500" : "text-gray-500 hover:text-gray-700"}`}
          onClick={() => setActiveTab("flags")}
        >
          <div className="flex items-center justify-center">
            <Flag size={14} className="mr-1" />
            Flags
          </div>
        </button>
        <button
          className={`flex-1 py-2 px-2 text-xs font-medium ${activeTab === "prescreening" ? "bg-blue-50 text-blue-600 border-b-2 border-blue-500" : "text-gray-500 hover:text-gray-700"}`}
          onClick={() => setActiveTab("prescreening")}
        >
          <div className="flex items-center justify-center">
            <ClipboardList size={14} className="mr-1" />
            Progress
          </div>
        </button>
        <button
          className={`flex-1 py-2 px-2 text-xs font-medium ${activeTab === "multiStudy" ? "bg-blue-50 text-blue-600 border-b-2 border-blue-500" : "text-gray-500 hover:text-gray-700"}`}
          onClick={() => setActiveTab("multiStudy")}
        >
          <div className="flex items-center justify-center">
            <GitBranch size={14} className="mr-1" />
            Matching
          </div>
        </button>
        <button
          className={`flex-1 py-2 px-2 text-xs font-medium ${activeTab === "questionnaireAnswers" ? "bg-blue-50 text-blue-600 border-b-2 border-blue-500" : "text-gray-500 hover:text-gray-700"}`}
          onClick={() => setActiveTab("questionnaireAnswers")}
        >
          <div className="flex items-center justify-center">
            <CheckCircle2 size={14} className="mr-1" />
            Submit
          </div>
        </button>
      </div>

      {/* Tab content */}
      <div className="overflow-y-auto max-h-[70vh]">
        {activeTab === "flags" && <FeatureFlagsTab />}

        {activeTab === "prescreening" && <PrescreeningTab />}

        {activeTab === "multiStudy" && <MultiStudyDebugTab />}

        {activeTab === "questionnaireAnswers" && (
          <QuestionnaireAnswersToSubmitTab />
        )}
      </div>
    </div>
  );
}
